import { useEffect, type RefObject } from "react";

export function focusable(node: HTMLElement): HTMLElement[] {
  return [...node.querySelectorAll<HTMLElement>("button, textarea, input, select, a[href]")].filter(
    (item) => !item.hasAttribute("disabled") && item.tabIndex !== -1,
  );
}

export function useDialogFocusTrap(dialog: RefObject<HTMLDialogElement | null>, onClose: () => void, focusSelector?: string) {
  const onCloseRef = { current: onClose };
  onCloseRef.current = onClose;
  useEffect(() => {
    const node = dialog.current;
    if (!node) return;
    node.querySelector<HTMLElement>(focusSelector ?? "button, input, textarea")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusable(node);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const active = node.ownerDocument.activeElement;
      const inside = active instanceof HTMLElement && node.contains(active);
      if (event.shiftKey && (!inside || active === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (!inside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    };
    node.addEventListener("keydown", onKey);
    return () => node.removeEventListener("keydown", onKey);
  }, [dialog, focusSelector]);
}
